import classNames from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Spinner from '../../../common/components/misc/Spinner';
import * as Actions from '../../../common/services/tyre/Actions';
import * as TyreService from '../../../common/services/tyre/Service';
import { LookupValueDTO } from '../../../common/types/dto/LookupValueDTO';
import { TyreDTO } from '../../../common/types/dto/TyreDTO';
import { TyreLOVDTO } from '../../../common/types/dto/TyreLOVDTO';
import { RootState } from '../../base/RootReducer';
import { config } from '../../common/';
import { AppThunkDispatch } from '../../common/types';
import SimulatorResults from './SimulatorResults';

interface SimulatorState {
    tyreSeccaoList: TyreLOVDTO[];
    tyreSerieList: TyreLOVDTO[];
    tyreJanteList: TyreLOVDTO[];
    tyreList: TyreDTO[];
    seccao: string;
    serie: string;
    jante: string;
    filterOrdenacao: string;
    icList: string[];
    filterIC: string;
    cvList: string[];
    filterCV: string;
    versaoList: string[];
    filterVersao: string;
    brandList: string[];
    filterBrand: string;
    showFilters: boolean;
}

interface ReduxStateProps {
    isCommunicatingSeccao: boolean;
    getTyreSeccao: TyreLOVDTO[];
    isCommunicatingSerie: boolean;
    getTyreSerie: TyreLOVDTO[];
    isCommunicatingJante: boolean;
    getTyreJante: TyreLOVDTO[];
    isCommunicatingList: boolean;
    getTyreList: TyreDTO[];

    dispatch: AppThunkDispatch;
}

interface ReduxDispatchProps {
    tyreActions: typeof Actions;
    tyreServiceActions: TyreService.ServiceActions;
}

type SimulatorProperties =
    ReduxStateProps
    & ReduxDispatchProps;

const Simulator: React.FC<SimulatorProperties> = (Props) => {
    const initialState: SimulatorState = {
        tyreSeccaoList: [],
        tyreSerieList: [],
        tyreJanteList: [],
        tyreList: [],
        seccao: '',
        serie: '',
        jante: '',
        filterOrdenacao: 'M',
        icList: [],
        filterIC: '',
        cvList: [],
        filterCV: '',
        versaoList: [],
        filterVersao: '',
        brandList: [],
        filterBrand: '',
        showFilters: false,
    };
    const [state, setState]: [SimulatorState, React.Dispatch<any>] = useReducer(
        (oldState, newState) => ({ ...oldState, ...newState }), initialState,
    );
    const { t } = useTranslation();

    useEffect(() => {
        Props.tyreServiceActions.getTyreSeccao();
    }, []);

    useEffect(() => {
        if (Props.getTyreSeccao) {
            setState({ tyreSeccaoList: Props.getTyreSeccao });
        }
    }, [Props.getTyreSeccao]);

    useEffect(() => {
        if (Props.getTyreSerie) {
            setState({ tyreSerieList: Props.getTyreSerie });
        }
    }, [Props.getTyreSerie]);

    useEffect(() => {
        if (Props.getTyreJante) {
            setState({ tyreJanteList: Props.getTyreJante });
        }
    }, [Props.getTyreJante]);

    useEffect(() => {
        if (Props.getTyreList) {
            setState({
                tyreList: Props.getTyreList,
                icList: [...new Set(Props.getTyreList.map((a) => a.ic))],
                cvList: [...new Set(Props.getTyreList.map((a) => a.cv))],
                versaoList: [...new Set(Props.getTyreList.map((a) => a.versao))],
                brandList: [...new Set(Props.getTyreList.map((a) => a.tyreBrandName))],
            });
        }
    }, [Props.getTyreList]);

    function resetFilters() {
        setState({
            filterOrdenacao: 'M',
            icList: [],
            filterIC: '',
            cvList: [],
            filterCV: '',
            versaoList: [],
            filterVersao: '',
            brandList: [],
            filterBrand: '',
            tyreList: [],
        });
    }

    function onSeccaoChange(selected: LookupValueDTO<string>) {
        setState({
            seccao: selected.value,
            serie: '',
            jante: '',
        });
        resetFilters();
        Props.tyreServiceActions.getTyreSerie(selected.value);
    }

    function onSerieChange(selected: LookupValueDTO<string>) {
        setState({
            serie: selected.value,
            jante: '',
        });
        resetFilters();
        Props.tyreServiceActions.getTyreJante(state.seccao, selected.value);
    }

    function onJanteChange(selected: LookupValueDTO<string>) {
        setState({ jante: selected.value });
        resetFilters();
        Props.tyreServiceActions.getTyreList(state.seccao, state.serie, selected.value);
    }

    function onFilterOrdenacaoChange(selected: LookupValueDTO<string>) {
        if (selected) {
            setState({ filterOrdenacao: selected.value });
        } else {
            setState({ filterOrdenacao: 'M' });
        }
    }

    function onFilterICChange(selected: LookupValueDTO<string>) {
        if (selected) {
            setState({ filterIC: selected.value });
        } else {
            setState({ filterIC: '' });
        }
    }

    function onFilterCVChange(selected: LookupValueDTO<string>) {
        if (selected) {
            setState({ filterCV: selected.value });
        } else {
            setState({ filterCV: '' });
        }
    }

    function onFilterVersaoChange(selected: LookupValueDTO<string>) {
        if (selected) {
            setState({ filterVersao: selected.value });
        } else {
            setState({ filterVersao: '' });
        }
    }

    function onFilterBrandChange(selected: LookupValueDTO<string>) {
        if (selected) {
            setState({ filterBrand: selected.value });
        } else {
            setState({ filterBrand: '' });
        }
    }

    function showFilters() {
        setState({ showFilters: !state.showFilters });
    }

    return (
        <React.Fragment>
            <div className={classNames(['grid-container', 'fluid', 'margin-top-2'])}>
                <div className={classNames(['grid-x', 'grid-margin-x'])}>
                    <div className={classNames(['cell', 'medium-12', 'margin-bottom-2'])}>
                        <p className={'text-bold'}>{t('tyre:select-measures')}</p>
                    </div>
                    <div className={classNames(['cell', 'medium-6', 'small-12', 'large-3'])}>
                        <label htmlFor="seccao" className={classNames(['form-label', 'distritosReparadores'])}>Secção *
                            <div className={'selectwrapper'}>
                                <select id="seccao" name="seccao" className={classNames(['form-control', 'base-select'])} onChange={(x) => onSeccaoChange(x.target)} disabled={Props.isCommunicatingSeccao || state.tyreSeccaoList.length === 0}>
                                    <option value="">{t('tyre:select')}</option>
                                    {state.tyreSeccaoList.map((x, id) => {
                                        if (x.value === undefined || x.value === '' || x.value === null) {
                                            return null;
                                        }
                                        return <option key={'seccao' + id} value={x.value || '-1'}>{x.value}</option>;
                                    })}
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className={classNames(['cell', 'medium-6', 'small-12', 'large-3'])}>
                        <label htmlFor="serie" className={classNames(['form-label', 'distritosReparadores'])}>Série *
                            <div className={'selectwrapper'}>
                                <select id="serie" name="serie" className={classNames(['form-control', 'base-select'])} onChange={(x) => onSerieChange(x.target)} disabled={Props.isCommunicatingSerie || state.tyreSerieList.length === 0 || state.seccao === ''}>
                                    <option value="">{t('tyre:select')}</option>
                                    {state.tyreSerieList.map((x, id) => {
                                        if (x.value === undefined || x.value === '' || x.value === null) {
                                            return null;
                                        }
                                        return <option key={'serie' + id} value={x.value || '-1'}>{x.value}</option>;
                                    })}
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className={classNames(['cell', 'medium-6', 'small-12', 'large-3'])}>
                        <label htmlFor="jante" className={classNames(['form-label', 'distritosReparadores'])}>Jante *
                            <div className={'selectwrapper'}>
                                <select id="jante" name="jante" className={classNames(['form-control', 'base-select'])} onChange={(x) => onJanteChange(x.target)} disabled={Props.isCommunicatingJante || state.tyreJanteList.length === 0 || state.serie === ''}>
                                    <option value="">{t('tyre:select')}</option>
                                    {state.tyreJanteList.map((x, id) => {
                                        if (x.value === undefined || x.value === '' || x.value === null) {
                                            return null;
                                        }
                                        return <option key={'jante' + id} value={x.value || '-1'}>{x.value}</option>;
                                    })}
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className={classNames(['cell', 'medium-6', 'small-12', 'large-3'])}>
                        <label htmlFor="ordenacao" className={classNames(['form-label', 'distritosReparadores'])}>Ordenação
                            <div className={'selectwrapper'}>
                                <select id="ordenacao" name="ordenacao" className={classNames(['form-control', 'base-select'])} onChange={(x) => onFilterOrdenacaoChange(x.target)} value={state.filterOrdenacao} disabled={state.jante === '' || state.tyreList.length === 0}>
                                    <option value="M">Modelo</option>
                                    <option value="P">Preço</option>
                                    <option value="B">Marca</option>
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className={classNames(['cell', 'medium-6', 'small-12', 'large-3'])} style={{ display: state.showFilters ? 'block' : 'none' }}>
                        <label htmlFor="indicecarga" className={classNames(['form-label', 'distritosReparadores'])}>Indice de carga
                            <div className={'selectwrapper'}>
                                <select id="indicecarga" name="indicecarga" className={classNames(['form-control', 'base-select'])} onChange={(x) => onFilterICChange(x.target)} disabled={state.jante === '' || state.tyreList.length === 0}>
                                    <option value="">Todos</option>
                                    {state.icList.map((x, id) => {
                                        if (x === undefined || x === '') {
                                            return null;
                                        }
                                        return <option key={'ic' + id} value={x || '-1'}>{x}</option>;
                                    })}
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className={classNames(['cell', 'medium-6', 'small-12', 'large-3'])} style={{ display: state.showFilters ? 'block' : 'none' }}>
                        <label htmlFor="codvelocidade" className={classNames(['form-label', 'distritosReparadores'])}>Código de velocidade
                            <div className={'selectwrapper'}>
                                <select id="codvelocidade" name="codvelocidade" className={classNames(['form-control', 'base-select'])} onChange={(x) => onFilterCVChange(x.target)} disabled={state.jante === '' || state.tyreList.length === 0}>
                                    <option value="">Todos</option>
                                    {state.cvList.map((x, id) => {
                                        if (x === undefined || x === '') {
                                            return null;
                                        }
                                        return <option key={'ic' + id} value={x || '-1'}>{x}</option>;
                                    })}
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className={classNames(['cell', 'medium-6', 'small-12', 'large-3'])} style={{ display: state.showFilters ? 'block' : 'none' }}>
                        <label htmlFor="runflat" className={classNames(['form-label', 'distritosReparadores'])}>Tecnologia Runflat
                            <div className={'selectwrapper'}>
                                <select id="runflat" name="runflat" className={classNames(['form-control', 'base-select'])} onChange={(x) => onFilterVersaoChange(x.target)} disabled={state.jante === '' || state.tyreList.length === 0}>
                                    <option value="">Todos</option>
                                    {state.versaoList.map((x, id) => {
                                        if (x === undefined || x === '') {
                                            return null;
                                        }
                                        return <option key={'ic' + id} value={x || '-1'}>{x}</option>;
                                    })}
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className={classNames(['cell', 'medium-6', 'small-12', 'large-3'])} style={{ display: state.showFilters ? 'block' : 'none' }}>
                        <label htmlFor="marca" className={classNames(['form-label', 'distritosReparadores'])}>Marca
                            <div className={'selectwrapper'}>
                                <select id="marca" name="marca" className={classNames(['form-control', 'base-select'])} onChange={(x) => onFilterBrandChange(x.target)} disabled={state.jante === '' || state.tyreList.length === 0}>
                                    <option value="">Todos</option>
                                    {state.brandList.map((x, id) => {
                                        if (x === undefined || x === '') {
                                            return null;
                                        }
                                        return <option key={'ic' + id} value={x || '-1'}>{x}</option>;
                                    })}
                                </select>
                            </div>
                        </label>
                    </div>
                    <div className={classNames(['cell', 'medium-12', 'grid-x', 'align-center'])}>
                        <a onClick={() => showFilters()} className={classNames(['more-options-show', 'filter-btn'])} style={{ fontWeight: 'bold', fontSize: '16px', display: 'inline' }}>
                            {state.showFilters ? 'Menos filtros' : 'Mais filtros'}
                        </a>
                    </div>
                </div>
            </div>
            <div className={'margin-top-2'}>
                <SimulatorResults
                    tyreList={state.tyreList}
                    filterBrand={state.filterBrand}
                    filterCV={state.filterCV}
                    filterIC={state.filterIC}
                    filterOrdenacao={state.filterOrdenacao}
                    filterVersao={state.filterVersao}
                />
            </div>
            {(Props.isCommunicatingJante || Props.isCommunicatingList || Props.isCommunicatingSeccao || Props.isCommunicatingSerie) && <Spinner />}
        </React.Fragment>
    );
};

const mapStateToProps = (store: RootState) => ({
    isCommunicatingSeccao: store.tyreState.getTyreSeccao.isCommunicating,
    getTyreSeccao: store.tyreState.getTyreSeccao.data,

    isCommunicatingSerie: store.tyreState.getTyreSerie.isCommunicating,
    getTyreSerie: store.tyreState.getTyreSerie.data,

    isCommunicatingJante: store.tyreState.getTyreJante.isCommunicating,
    getTyreJante: store.tyreState.getTyreJante.data,

    isCommunicatingList: store.tyreState.getTyreList.isCommunicating,
    getTyreList: store.tyreState.getTyreList.data,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    tyreActions: bindActionCreators(Actions, dispatch),
    tyreServiceActions: TyreService.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(Simulator);
